import { courses as catalog } from '../data/courses'
import { courseImages } from '../components/landing/landingData'

const MISSING = 'missing'

const TOOL_LEXICON = [
  'Adobe Photoshop',
  'Photoshop',
  'Lightroom',
  'Adobe Illustrator',
  'Illustrator',
  'InDesign',
  'Adobe Premiere Pro',
  'Premiere Pro',
  'After Effects',
  'DaVinci Resolve',
  'Final Cut Pro',
  'Figma',
  'Adobe XD',
  'Blender',
  'Nuke',
  'ChatGPT',
  'Midjourney',
  'Adobe Firefly',
  'Generative Fill',
  'Neural Filters',
  'Facebook Ads',
  'Google Ads',
]

const EQUIPMENT_LEXICON = [
  'DSLR',
  'mirrorless cameras',
  'professional video cameras',
  'gimbal',
  'DJI drones',
  'cinema cameras',
  'green screen',
]

function sourceText(course) {
  return [
    course.description,
    ...(course.advantages || []),
    ...(course.whatYouCanDo || []),
    ...(course.jobOpportunities || []),
  ].join(' ')
}

function extractNamed(text, lexicon) {
  const found = []
  lexicon.forEach((term) => {
    const hit = text.toLowerCase().includes(term.toLowerCase())
    if (!hit) return
    const shorterCovered = found.some((existing) => existing.toLowerCase().includes(term.toLowerCase()))
    const replaces = found.findIndex((existing) => term.toLowerCase().includes(existing.toLowerCase()) && term.length > existing.length)
    if (replaces >= 0) found.splice(replaces, 1, term)
    else if (!shorterCovered) found.push(term)
  })
  return found
}

function relatedCodes(course) {
  return catalog
    .filter((item) => item.category === course.category && item.code !== course.code)
    .map((item) => item.code)
}

export function formatLiveFee(amount) {
  if (amount == null || amount === '') return null
  const numeric = Number(amount)
  if (Number.isNaN(numeric)) return null
  return `${Math.round(numeric).toLocaleString('en-UG')} UGX`
}

export function formatLiveDuration(live) {
  if (!live) return null
  const weeks = Number(live.duration_weeks)
  if (Number.isInteger(weeks) && weeks > 0) {
    return weeks === 1 ? '1 week' : `${weeks} weeks`
  }
  if (live.duration && String(live.duration).trim()) {
    return String(live.duration).trim()
  }
  return null
}

export function normalizeCourse(course) {
  const text = sourceText(course)
  const software = extractNamed(text, TOOL_LEXICON)
  const equipment = extractNamed(text, EQUIPMENT_LEXICON)
  const missing = []

  if (!software.length) missing.push('software')
  if (!equipment.length) missing.push('equipment')
  missing.push('perCourseRequirements')
  missing.push('perCourseRegistrationFee')
  missing.push('perCoursePaymentPlan')

  return {
    code: course.code,
    name: course.title,
    title: course.title,
    category: course.category,
    description: course.description,
    duration: null,
    price: null,
    fee: null,
    skillLevel: course.level,
    level: course.level,
    learningOutcomes: course.whatYouCanDo || [],
    whatYouCanDo: course.whatYouCanDo || [],
    advantages: course.advantages || [],
    careerOutcomes: course.jobOpportunities || [],
    jobOpportunities: course.jobOpportunities || [],
    software: software.length ? { items: software, status: 'extracted', source: 'frontend/src/data/courses.js marketing copy' } : { items: [], status: MISSING },
    equipment: equipment.length ? { items: equipment, status: 'extracted', source: 'frontend/src/data/courses.js marketing copy' } : { items: [], status: MISSING },
    relatedCourseCodes: relatedCodes(course),
    image: courseImages[course.code] || courseImages.default,
    url: course.url,
    source: 'frontend/src/data/courses.js',
    feeSource: 'unavailable',
    durationSource: 'unavailable',
    is_active: null,
    liveId: null,
    missing,
  }
}

const marketingCourses = catalog.map(normalizeCourse)
let liveByCode = new Map()

export function applyLiveCourses(rows = []) {
  liveByCode = new Map(
    rows
      .filter((row) => row?.course_code)
      .map((row) => [row.course_code, row])
  )
}

function mergeCourse(marketing, live) {
  const base = marketing
    ? { ...marketing }
    : {
        code: live.course_code,
        name: live.course_name,
        title: live.course_name,
        category: live.category || '',
        description: live.description || '',
        skillLevel: 'Beginner',
        level: 'Beginner',
        learningOutcomes: [],
        whatYouCanDo: [],
        advantages: [],
        careerOutcomes: [],
        jobOpportunities: [],
        relatedCourseCodes: [],
        image: courseImages.default,
        missing: ['marketingCopy'],
        software: { items: [], status: MISSING },
        equipment: { items: [], status: MISSING },
      }

  if (!live) {
    return {
      ...base,
      fee: null,
      price: null,
      duration: null,
      feeSource: 'unavailable',
      durationSource: 'unavailable',
    }
  }

  const liveFee = formatLiveFee(live.fee)
  const liveDuration = formatLiveDuration(live)

  return {
    ...base,
    name: live.course_name || base.name,
    title: live.course_name || base.title,
    category: live.category || base.category,
    description: live.description || base.description,
    fee: liveFee,
    price: liveFee,
    duration: liveDuration,
    feeSource: liveFee ? 'postgresql' : 'unavailable',
    durationSource: liveDuration ? 'postgresql' : 'unavailable',
    is_active: live.is_active,
    liveId: live.id,
    source: 'postgresql + marketing catalog',
  }
}

export function getMergedCourses() {
  const codes = new Set([
    ...marketingCourses.map((course) => course.code),
    ...liveByCode.keys(),
  ])

  return [...codes]
    .map((code) => mergeCourse(
      marketingCourses.find((course) => course.code === code) || null,
      liveByCode.get(code) || null
    ))
    .filter((course) => course.is_active !== false)
}

export const knowledgeCourses = marketingCourses

export function findCourse(code) {
  return getMergedCourses().find((course) => course.code === code) || null
}

export function coursesByCategory(category) {
  return getMergedCourses().filter((course) => course.category === category)
}

export function coursesByInterest(interestId) {
  const map = {
    photos: ['Photography'],
    videos: ['Video Production'],
    designs: ['Graphic Design'],
    animations: ['Animation'],
    ai: ['AI & Technology'],
    web: ['UI/UX Design'],
    unsure: ['Multimedia', 'Social Media', 'Photography'],
  }
  const categories = map[interestId] || []
  return getMergedCourses().filter((course) => categories.includes(course.category))
}

export function matchCourseFromQuery(query) {
  const text = String(query || '').toLowerCase()
  if (!text) return null
  const courses = getMergedCourses()
  return courses.find((course) => (
    text.includes(String(course.code || '').toLowerCase())
    || text.includes(String(course.name || '').toLowerCase())
    || (course.category && text.includes(course.category.toLowerCase()))
  )) || null
}

export const courseFieldCoverage = {
  catalogSource: 'frontend/src/data/courses.js (marketing copy only)',
  liveSource: 'GET /api/auth/courses (PostgreSQL)',
  commercialAuthority: 'postgresql',
  educationalAuthority: 'published course knowledge (backend)',
  admissionsAuthority: 'academy knowledge (backend)',
}
